import type { Ancestor } from "@/entities/ancestor";
import type { InterventionTemplateMinimal } from "@/entities/intervention-template";
import type { Nullable } from "@/types";

import { createElement } from "react";

import { getLastAssetAncestor } from "@/entities/ancestor";
import { useGetAsset } from "@/entities/asset";
import { isValidObject, isValueObject } from "@/utilities/object";

import { InterventionTemplateSelectLabel } from "./intervention-template-select-label";

/**
 * Extracts the intervention template ID from a form field value.
 * Accepts: primitive (string/number), object with `value` property.
 *
 * @param fieldValue - The value from the form field (unknown type)
 * @returns The template ID as string or number, or null if not found
 */
export function getInterventionTemplateId(
  fieldValue: unknown
): string | number | null {
  if (isValueObject(fieldValue)) {
    if (
      typeof fieldValue.value === "string" ||
      typeof fieldValue.value === "number"
    ) {
      return fieldValue.value || null;
    }

    if (
      "id" in fieldValue &&
      (typeof fieldValue.id === "string" || typeof fieldValue.id === "number")
    ) {
      return fieldValue.id || null;
    }

    return null;
  }

  if (
    isValidObject(fieldValue) &&
    "id" in fieldValue &&
    (typeof fieldValue.id === "string" || typeof fieldValue.id === "number")
  ) {
    return fieldValue.id || null;
  }

  if (typeof fieldValue === "string" || typeof fieldValue === "number") {
    return fieldValue || null;
  }

  return null;
}

/**
 * Builds a Select value compatible with `labelInValue` from an intervention template.
 *
 * @param template - Existing intervention template (or null/undefined)
 * @returns A labelInValue object or undefined when there is no template
 */
export function getInterventionTemplateSelectFieldValue(
  template: Nullable<InterventionTemplateMinimal>
) {
  if (!template) {
    return undefined;
  }

  return {
    value: template.id,
    label: createElement(InterventionTemplateSelectLabel, { template }),
  };
}

function isInterventionTemplateMinimal(
  value: unknown
): value is InterventionTemplateMinimal {
  if (!isValidObject(value) || !("id" in value)) {
    return false;
  }

  const hasId = typeof value.id === "string" || typeof value.id === "number";
  const hasName = "name" in value && typeof value.name === "string";
  const hasState = "state" in value && typeof value.state === "string";
  const hasVersion =
    "version" in value &&
    (typeof value.version === "string" || typeof value.version === "number");

  return hasId && hasName && hasState && hasVersion;
}

export function formatInterventionTemplateSelectValue(fieldValue: unknown) {
  if (!fieldValue) {
    return undefined;
  }

  if (
    isValueObject(fieldValue) &&
    (typeof fieldValue.value === "string" ||
      typeof fieldValue.value === "number")
  ) {
    return fieldValue;
  }

  if (isInterventionTemplateMinimal(fieldValue)) {
    return getInterventionTemplateSelectFieldValue(fieldValue);
  }

  if (typeof fieldValue === "string" || typeof fieldValue === "number") {
    return { value: fieldValue };
  }

  return undefined;
}

export function useInterventionTemplateValue(ancestors: Ancestor[]) {
  const assetAncestor = getLastAssetAncestor(ancestors);
  const { asset, isLoading } = useGetAsset(assetAncestor?.id);

  return {
    value: getInterventionTemplateSelectFieldValue(
      asset?.default_template ?? null
    ),
    defaultTemplate: asset?.default_template,
    type: asset?.default_template?.intervention_type_id,
    isLoading,
  };
}
